import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UserPlus, KeyRound, Trash2, ShieldCheck } from "lucide-react";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user object
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Add form
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  // Reset form
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);

  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/admin/users");
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    setAddBusy(true);
    try {
      await api.post("/auth/admin/users", {
        name: addName.trim(),
        email: addEmail.trim(),
        password: addPassword,
        role: "staff",
      });
      toast.success("User added");
      setAddOpen(false);
      setAddName(""); setAddEmail(""); setAddPassword("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      await api.patch(`/auth/admin/users/${resetTarget.id}/password`, { password: resetPw });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setResetPw("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/auth/admin/users/${deleteTarget.id}`);
      toast.success("User removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Team Members</h1>
          <p className="text-sm text-slate-500 mt-1">
            Add, remove and reset passwords for staff accounts.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="bg-sky-500 hover:bg-sky-600 text-white"
          data-testid="add-user-button"
        >
          <UserPlus size={16} className="mr-2" /> Add member
        </Button>
      </div>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  <Loader2 className="animate-spin inline mr-2" size={16} /> Loading…
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No members yet.
                </td></tr>
              ) : users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                      u.role === "owner"
                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-slate-100 text-slate-700"
                    }`}>
                      {u.role === "owner" && <ShieldCheck size={12} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setResetTarget(u); setResetPw(""); }}
                        data-testid={`reset-password-${u.email}`}
                      >
                        <KeyRound size={14} className="mr-1" /> Reset password
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        disabled={u.role === "owner" || u.id === user?.id}
                        onClick={() => setDeleteTarget(u)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        aria-label={`Remove ${u.email}`}
                        title={u.role === "owner" ? "Owner accounts can't be removed" : `Remove ${u.email}`}
                        data-testid={`delete-user-${u.email}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>Add a team member</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitAdd} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="add-name">Full name</Label>
              <Input id="add-name" required value={addName} onChange={(e) => setAddName(e.target.value)}
                     placeholder="Dr. Jane Doe" data-testid="add-user-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-email">Email</Label>
              <Input id="add-email" type="email" required value={addEmail}
                     onChange={(e) => setAddEmail(e.target.value)}
                     placeholder="jane@clinic.com" data-testid="add-user-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-password">Temporary password</Label>
              <Input id="add-password" required minLength={6} value={addPassword}
                     onChange={(e) => setAddPassword(e.target.value)}
                     placeholder="Min 6 characters" data-testid="add-user-password" />
              <p className="text-xs text-slate-500">Share this with the user; they can change it after signing in.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={addBusy} className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="submit-add-user">
                {addBusy ? <Loader2 className="animate-spin" size={16} /> : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReset} className="space-y-4">
            <p className="text-sm text-slate-600">
              Setting a new password for <span className="font-medium">{resetTarget?.email}</span>.
              Share it with them privately.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-pw">New password</Label>
              <Input id="reset-pw" required minLength={6} value={resetPw}
                     onChange={(e) => setResetPw(e.target.value)}
                     placeholder="Min 6 characters" data-testid="reset-password-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button>
              <Button type="submit" disabled={resetBusy} className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="submit-reset-password">
                {resetBusy ? <Loader2 className="animate-spin" size={16} /> : "Reset password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Remove member?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            <span className="font-medium">{deleteTarget?.name}</span> ({deleteTarget?.email}) will lose
            access to the clinic immediately. This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={submitDelete} disabled={deleteBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-user"
            >
              {deleteBusy ? <Loader2 className="animate-spin" size={16} /> : "Remove member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
